import * as React from "react";
import Select, {
	type ClassNamesConfig,
	type GroupBase,
	type MultiValue,
} from "react-select";
import { cn } from "@/lib/utils";
import type { SearchableSelectOption } from "@/components/ui/searchable-select";

export interface MultiSelectProps {
	value: string[];
	onValueChange: (value: string[]) => void;
	options: SearchableSelectOption[];
	placeholder?: string;
	emptyMessage?: string;
	disabled?: boolean;
	className?: string;
	contentClassName?: string;
	size?: "sm" | "md";
	ariaLabel?: string;
}

const PORTAL_TARGET =
	typeof document !== "undefined" ? document.body : undefined;

type RSGroup = GroupBase<SearchableSelectOption>;

export function MultiSelect({
	value,
	onValueChange,
	options,
	placeholder,
	emptyMessage = "No results",
	disabled = false,
	className,
	contentClassName,
	size = "md",
	ariaLabel,
}: MultiSelectProps) {
	const selectedOptions = React.useMemo(
		() => options.filter((o) => value.includes(o.value)),
		[options, value],
	);

	const classNames: ClassNamesConfig<SearchableSelectOption, true, RSGroup> =
		React.useMemo(
			() => ({
				control: ({ isFocused, isDisabled }) =>
					cn(
						"flex w-full items-center justify-between rounded-lg border bg-surface px-3 text-sm text-foreground transition",
						size === "sm" ? "min-h-8 py-0.5" : "min-h-9 py-1",
						isFocused
							? "border-primary ring-2 ring-primary"
							: "border-border-strong",
						isDisabled && "cursor-not-allowed opacity-50",
						className,
					),
				valueContainer: () => "gap-1 p-0 flex-wrap",
				placeholder: () => "text-muted",
				multiValue: () =>
					"rounded-md bg-primary-subtle text-primary px-1.5 py-0.5",
				multiValueLabel: () => "text-xs text-primary",
				multiValueRemove: () =>
					"text-primary hover:bg-primary/10 rounded-sm cursor-pointer",
				input: () =>
					"text-sm text-foreground [&_input]:focus:ring-0 [&_input]:focus:outline-none",
				indicatorsContainer: () => "gap-1",
				indicatorSeparator: () => "hidden",
				dropdownIndicator: ({ isFocused }) =>
					cn(
						"text-muted hover:text-foreground px-1",
						isFocused && "text-foreground",
					),
				clearIndicator: () =>
					"text-muted hover:text-foreground cursor-pointer px-1",
				menu: () =>
					cn(
						"z-50 mt-1 overflow-hidden rounded-lg border border-border bg-surface text-foreground shadow-[0px_4px_12px_rgba(0,0,0,0.06)]",
						contentClassName,
					),
				menuList: () => "p-1 max-h-72",
				option: ({ isFocused, isSelected, isDisabled }) =>
					cn(
						"relative flex w-full cursor-default select-none items-center rounded-md py-1.5 px-2 text-xs! outline-none",
						isFocused && !isSelected && "bg-muted-subtle",
						isSelected && "bg-primary-subtle text-primary",
						isDisabled && "pointer-events-none opacity-50",
					),
				noOptionsMessage: () => "py-6 text-center text-sm text-muted",
			}),
			[size, className, contentClassName],
		);

	const filterOption = React.useMemo(
		() =>
			(candidate: { data: SearchableSelectOption }, raw: string) => {
				const q = raw.trim().toLowerCase();
				if (!q) return true;
				const d = candidate.data;
				return (
					d.label.toLowerCase().includes(q) ||
					(d.description ?? "").toLowerCase().includes(q)
				);
			},
		[],
	);

	return (
		<Select<SearchableSelectOption, true, RSGroup>
			aria-label={ariaLabel}
			isMulti
			value={selectedOptions}
			onChange={(opts: MultiValue<SearchableSelectOption>) =>
				onValueChange(opts.map((o) => o.value))
			}
			options={options}
			isOptionDisabled={(o) => !!o.disabled}
			isDisabled={disabled}
			placeholder={placeholder}
			noOptionsMessage={() => emptyMessage}
			closeMenuOnSelect={false}
			hideSelectedOptions={false}
			unstyled
			classNames={classNames}
			filterOption={filterOption}
			menuPortalTarget={PORTAL_TARGET}
			menuPosition="fixed"
			styles={{
				menuPortal: (base) => ({ ...base, zIndex: 60 }),
			}}
			theme={(t) => ({
				...t,
				borderRadius: 8,
				colors: {
					...t.colors,
					primary: "#0058be",
					primary25: "#eff5ff",
					primary50: "#dbeafe",
					primary75: "#bfdbfe",
					neutral20: "#c2c6d6",
					neutral30: "#c2c6d6",
				},
			})}
		/>
	);
}
